import type { ApiClientConfig } from './types'
import * as Sentry from '@sentry/vue'
import { router } from '@/app/providers/router'
import { useAuthStore } from '@/entities/auth/model/auth.store'
import { getAuthTokens, setAuthTokens } from '@/shared/lib/cookies'
import { clearAllStorageOnLogout, getCookie } from '@/shared/lib/utils'
import { handleApiError } from '@/shared/lib/utils/errorHandler'
import { HttpError } from './errors'
import {
  addAuthBreadcrumb,
  captureForcedLogout,
} from './reportApiError'

// Source of truth is VITE_API_BASE_URL from .env / .env.local. The previous
// hardcoded production URL bypassed local overrides; restoring the env-driven
// version so docker / local dev hit the right backend.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'
// No initialization needed for the event-based error handler

/**
 * The core, private fetcher function.
 * It's not meant to be used directly by features.
 */
export async function http<T>(
  url: string,
  config: ApiClientConfig = {},
): Promise<T> {
  // 1. Get auth token - try proactive refresh if access token missing but refresh token exists
  let token = getCookie('access_token')

  // Proactive token refresh: if no access token but refresh token exists, refresh first
  // This prevents requests from going out without auth header.
  // We swallow any non-OK result here — the request will just go out
  // unauthenticated and either succeed (public endpoint) or get 401, and the
  // reactive path below will handle it.
  if (!token) {
    const { refreshToken } = getAuthTokens()
    if (refreshToken) {
      const result = await refreshAccessToken()
      if (result.kind === 'ok') {
        token = result.accessToken
      }
    }
  }

  // 2. Create headers - optimize for simple requests to avoid CORS preflight
  const headers = new Headers()

  // Only add Content-Type for requests with body to avoid preflight
  if (config.data && !(config.data instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  // Add Accept header
  headers.set('Accept', 'application/json')

  // Add Authorization if token exists
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  // Add x-store-id header if available
  const storeId = getCookie('storeId')
  if (storeId) {
    headers.set('x-store-id', storeId)
  }

  // Add any additional headers from config
  if (config.headers) {
    Object.entries(config.headers).forEach(([key, value]) => {
      headers.set(key, value)
    })
  }

  // 3. Handle query parameters
  let fullUrl = `${API_BASE_URL}${url}`
  if (config.params) {
    const queryParams = new URLSearchParams()
    // Convert params to string values for URLSearchParams
    Object.entries(config.params).forEach(([key, value]) => {
      queryParams.append(key, String(value))
    })
    fullUrl += `?${queryParams.toString()}`
  }

  // 4. Handle body data - special case for FormData
  let bodyData = null
  if (config.data) {
    // If it's FormData, use it directly without stringifying
    if (config.data instanceof FormData) {
      bodyData = config.data
      // Remove content-type header as the browser will set it with the boundary parameter
      headers.delete('Content-Type')
    }
    else {
      // For regular objects, stringify as JSON
      bodyData = JSON.stringify(config.data)
    }
  }

  // 5. Check if this is a simple request (won't trigger preflight)
  const method = config.method || 'GET'
  // const isSimple = isSimpleRequest(method, headers)

  // if (isSimple) {
  //   console.log("HTTP - Simple request (no preflight):", {
  //     url: fullUrl,
  //     method,
  //     headers: Object.fromEntries(headers.entries())
  //   })
  // } else {
  //   console.log("HTTP - Complex request (may trigger preflight):", {
  //     url: fullUrl,
  //     method,
  //     headers: Object.fromEntries(headers.entries())
  //   })
  // }

  let response: Response

  try {
    response = await fetch(fullUrl, {
      method,
      headers,
      body: bodyData,
    })
  }
  catch (fetchError) {
    console.error('Network error during fetch:', fetchError)

    // Check if it's a 502 error from the error message
    const errorMessage
      = fetchError instanceof Error ? fetchError.message : String(fetchError)
    const looksLike502
      = errorMessage.includes('502') || errorMessage.includes('Bad Gateway')
    if (looksLike502 && !config.silent) {
      // Dispatch server error event (skipped for silent callers)
      const errorEvent = new CustomEvent('api-server-error', {
        detail: {
          status: 502,
          message: 'Server error 502. Try again later',
        },
        bubbles: true,
        cancelable: true,
      })
      window.dispatchEvent(errorEvent)
    }

    // Guaranteed Sentry capture for fetch-throws (network failure, CORS,
    // offline, nginx dropping the connection before responding). These
    // errors have no Response object so the network-layer 5xx branch
    // below never runs — capture them here or they are lost whenever the
    // caller wraps the call in a try/catch.
    //
    // We mark the error as "already reported" unconditionally so that
    // `reportApiError` (called downstream by TanStack Query's error path)
    // does not re-capture or re-classify it as a generic unknown error.
    ;(fetchError as Error & { _sentryReported?: boolean })._sentryReported = true

    // Two cases are NOT defects in our app and would only add noise:
    //   - AbortError: the caller cancelled (component unmount, route change,
    //     query dedupe). Forward-defensive — not currently used, but if any
    //     feature wires up an AbortSignal later this stays correct.
    //   - Offline: `navigator.onLine === false` means the device has no
    //     network. Not actionable from the frontend.
    // We still emit a breadcrumb so any *subsequent* captured event (e.g.
    // the user retries online and hits a real 500) has the failure context.
    const isAbort
      = fetchError instanceof Error && fetchError.name === 'AbortError'
    const isOffline
      = typeof navigator !== 'undefined' && navigator.onLine === false
    if (isAbort || isOffline) {
      Sentry.addBreadcrumb({
        category: 'http',
        level: 'info',
        message: isAbort ? 'fetch_aborted' : 'fetch_offline',
        data: { url: fullUrl, method },
      })
      throw fetchError
    }

    Sentry.captureException(fetchError, {
      level: 'error',
      tags: {
        error_type: 'network_failure',
        http_method: method,
        source: 'network_layer',
        likely_502: String(looksLike502),
        online: typeof navigator !== 'undefined' ? String(navigator.onLine) : 'unknown',
      },
      contexts: {
        request: {
          url: fullUrl,
          method,
        },
      },
      fingerprint: [
        'network-failure',
        method,
        safePathname(fullUrl),
        looksLike502 ? '502' : 'unknown',
      ],
    })

    // Re-throw the error for TanStack Query to handle
    throw fetchError
  }

  // 5. Handle the response
  if (!response.ok) {
    // Handle 5xx server errors immediately.
    // Respect `silent` so callers with their own recovery UX (e.g. autobidder
    // bid-link retry modal) don't show a generic "Сервер вернул ошибку" toast
    // on top of their dedicated retry dialog.
    if (
      response.status >= 500
      && response.status < 600
      && !config.silent
    ) {
      const errorEvent = new CustomEvent('api-server-error', {
        detail: {
          status: response.status,
          message: `Server error ${response.status}. Try again later`,
        },
        bubbles: true,
        cancelable: true,
      })
      window.dispatchEvent(errorEvent)
    }

    // Attempt token refresh for 401/403 once, then retry the original request.
    // We keep the refresh result around to decide below whether the final
    // failure (if any) should log the user out or be treated as transient.
    let refreshResult: RefreshResult | null = null
    if (response.status === 401 || response.status === 403) {
      refreshResult = await refreshAccessToken()
      if (refreshResult.kind === 'ok') {
        headers.set('Authorization', `Bearer ${refreshResult.accessToken}`)
        try {
          response = await fetch(fullUrl, { method, headers, body: bodyData })
        }
        catch (retryError) {
          console.error('Network error during retry:', retryError)
          throw retryError
        }
      }
    }

    if (!response.ok) {
      // If the response is still not OK, create our custom error
      const httpError = new HttpError(response, method, fullUrl)

      // Guaranteed Sentry capture for 5xx at the network layer. Doesn't rely
      // on the call going through TanStack Query's `queryCache.onError`, so
      // direct `apiClient.*` calls wrapped in `try/catch` are still reported.
      // Marked so `reportApiError` skips double-sending when the error does
      // flow through TanStack Query.
      if (response.status >= 500 && response.status < 600) {
        const pathname = safePathname(fullUrl)
        Sentry.captureException(httpError, {
          level: 'error',
          tags: {
            error_type: 'http_error',
            http_status: String(response.status),
            http_method: method,
            source: 'network_layer',
          },
          contexts: {
            request: {
              url: fullUrl,
              method,
              status_code: response.status,
            },
          },
          fingerprint: ['http-error', method, pathname, String(response.status)],
        })
        ;(httpError as HttpError & { _sentryReported?: boolean })._sentryReported = true
      }

      // Special handling for 417 status on /async/dashboard/ endpoint
      // Mark it as non-retryable for TanStack Query
      if (response.status === 417 && url.includes('/async/dashboard/')) {
        httpError.name = 'NonRetryableError'
      }

      // Handle 419/429 rate limit errors - emit event for cooldown handling
      if (response.status === 419 || response.status === 429) {
        try {
          const errorData = await httpError.parseError()
          const detail
            = (errorData as { detail?: string })?.detail || 'Rate limit exceeded'
          // Dispatch rate limit event
          const rateLimitEvent = new CustomEvent('api-rate-limit', {
            detail: { message: detail, url },
            bubbles: true,
            cancelable: true,
          })
          window.dispatchEvent(rateLimitEvent)
        }
        catch (err) {
          console.error(`Error parsing ${response.status} response`, err)
        }
      }

      // Decide whether to force a logout.
      //
      // Logout ONLY when we're CERTAIN the session is unrecoverable:
      //   - No refresh token at all, or
      //   - Refresh endpoint returned 401/403 (refresh token itself invalid), or
      //   - Refresh succeeded but the retried request STILL returned 401/403
      //     (user's access was revoked mid-session).
      //
      // If the refresh endpoint failed with a 5xx or network error
      // (`kind: 'transient'`), we keep the tokens and let the error propagate.
      // The user stays logged in and can retry — typical mobile hiccup.
      if (response.status === 401 || response.status === 403) {
        const shouldForceLogout
          = refreshResult !== null
            && (refreshResult.kind === 'invalid'
              || refreshResult.kind === 'no_refresh_token'
              || refreshResult.kind === 'ok')

        if (shouldForceLogout) {
          const reason
            = refreshResult!.kind === 'ok'
              ? 'retry_still_unauthorized'
              : refreshResult!.kind === 'invalid'
                ? 'refresh_invalid'
                : 'no_refresh_token'

          captureForcedLogout({
            reason,
            originalStatus: response.status,
            originalUrl: fullUrl,
            refreshStatus:
              refreshResult!.kind === 'invalid'
                ? refreshResult!.status
                : undefined,
            hadRefreshToken: refreshResult!.kind !== 'no_refresh_token',
          })

          try {
            const authStore = useAuthStore()
            authStore.logout()
            // Clear all storage on unauthorized access
            clearAllStorageOnLogout()
          }
          catch {
            // fallback: clear all storage manually if store is not available
            clearAllStorageOnLogout()
          }
          router.push({ name: 'Home' })
        }
        else {
          // Transient refresh failure — log a breadcrumb so the next captured
          // error has context, but keep the user signed in.
          addAuthBreadcrumb('auth_error_suppressed_transient', {
            status: response.status,
            url: fullUrl,
          })
        }
      }

      // Only show error notifications if not in silent mode
      if (!config.silent) {
        try {
          // Try to parse the error response
          const errorData = await httpError.parseError()

          // If we have error data, handle it (will check for visibility or structured format)
          if (errorData) {
            handleApiError(httpError)
          }
        }
        catch (err) {
          console.error('Error handling API error', err)
        }
      }

      // TanStack Query will catch this and put it in the `error` state
      throw httpError
    }
  }

  // If the response is OK but has no content (e.g., DELETE request)
  if (response.status === 204 || response.status === 205) {
    return null as T
  }

  // Parse the JSON response
  const data = await response.json()

  // Otherwise, just return the data
  return data as T
}

// ===== Token Refresh Logic =====
/**
 * Tagged result from `refreshAccessToken` so callers can distinguish a
 * definitive failure (refresh token itself invalid → must log out) from a
 * transient failure (server 5xx / network error → keep tokens, surface the
 * original error, let the user retry).
 *
 * Treating every refresh failure as "log out" — which we did before — caused
 * mobile users to get kicked to the landing page every time the refresh
 * endpoint hiccuped, which is common on flaky networks.
 */
export type RefreshResult
  = | { kind: 'ok', accessToken: string }
    | { kind: 'no_refresh_token' }
    | { kind: 'invalid', status: number }
    | { kind: 'transient', status?: number }

let tokenRefreshPromise: Promise<RefreshResult> | null = null

export async function refreshAccessToken(): Promise<RefreshResult> {
  if (tokenRefreshPromise)
    return tokenRefreshPromise

  tokenRefreshPromise = (async () => {
    const { refreshToken } = getAuthTokens()
    if (!refreshToken) {
      addAuthBreadcrumb('refresh_skipped_no_token')
      return { kind: 'no_refresh_token' } as const
    }

    addAuthBreadcrumb('refresh_attempt')

    let resp: Response
    try {
      resp = await fetch(`${API_BASE_URL}/token/refresh/`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: refreshToken }),
      })
    }
    catch (err) {
      // fetch threw — network error, DNS failure, CORS, offline, etc.
      addAuthBreadcrumb('refresh_network_error', {
        error: err instanceof Error ? err.message : String(err),
      })
      return { kind: 'transient' } as const
    }

    if (!resp.ok) {
      // 401 / 403 → refresh token is definitively invalid; we must log out.
      // 5xx or anything else → transient, keep the session alive.
      if (resp.status === 401 || resp.status === 403) {
        addAuthBreadcrumb('refresh_invalid', { status: resp.status })
        return { kind: 'invalid', status: resp.status } as const
      }

      addAuthBreadcrumb('refresh_transient_error', { status: resp.status })
      return { kind: 'transient', status: resp.status } as const
    }

    let data: { access?: string, refresh?: string }
    try {
      data = await resp.json()
    }
    catch (err) {
      addAuthBreadcrumb('refresh_parse_error', {
        error: err instanceof Error ? err.message : String(err),
      })
      return { kind: 'transient', status: resp.status } as const
    }

    const newAccess = data.access
    const newRefresh = data.refresh ?? refreshToken

    if (!newAccess) {
      addAuthBreadcrumb('refresh_missing_access_field')
      return { kind: 'transient', status: resp.status } as const
    }

    // Persist new tokens
    setAuthTokens(newAccess, newRefresh)
    addAuthBreadcrumb('refresh_success')
    return { kind: 'ok', accessToken: newAccess } as const
  })()
    .finally(() => {
      tokenRefreshPromise = null
    })

  return tokenRefreshPromise
}

function safePathname(url: string): string {
  try {
    return new URL(url).pathname
  }
  catch {
    return url
  }
}
